import { Share2 } from "lucide-react";
import { UserAvatar } from "../../../components/ui/avatar";
import { UserIdentity } from "../../../components/UserIdentity";
import { getUserIdentity } from "../../../lib/identity";

/**
 * Shared per-friend row used by both the Friends & Groups page and the
 * "Share availability" modal launched from the My Availability page.
 *
 * Actions are opt-in: pass only the handlers you want to render. The Friends
 * page uses all three (share + view + remove); the My Availability modal
 * only uses share.
 */
export function FriendShareRow({
  friend,
  isSharing,
  onShareClick,
  onViewClick,
  onRemoveClick,
  testIdPrefix = "share-availability",
}) {
  const handleShare = (event) => {
    event.stopPropagation();
    if (!friend.uid) return;
    onShareClick?.(friend);
  };
  const handleView = (event) => {
    event.stopPropagation();
    onViewClick?.(friend);
  };
  const handleRemove = (event) => {
    event.stopPropagation();
    onRemoveClick?.(friend);
  };
  // Secondary line shows the friend's CHOSEN public identifier (handle/@username,
  // or their email only if they picked the email type) — never the raw email of
  // a non-email user. getUserIdentity returns the email here only for an
  // email-type (or untyped/default) friend, matching UserIdentity's behavior.
  const { publicIdentifier, isDuplicate } = getUserIdentity(friend);
  const secondaryIdentifier =
    publicIdentifier && !isDuplicate ? publicIdentifier : null;
  const rowClickable = Boolean(onViewClick);
  const onRowClick = rowClickable ? () => onViewClick(friend) : undefined;
  const onRowKey = rowClickable
    ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onViewClick(friend);
        }
      }
    : undefined;

  return (
    <div
      key={friend.email}
      role={rowClickable ? "button" : undefined}
      tabIndex={rowClickable ? 0 : undefined}
      onClick={onRowClick}
      onKeyDown={onRowKey}
      className={
        rowClickable
          ? "flex cursor-pointer flex-col gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 text-xs transition-colors hover:bg-slate-50 sm:flex-row sm:items-center dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700/50"
          : "flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 text-xs sm:flex-row sm:items-center dark:border-slate-700 dark:bg-slate-800"
      }
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
          <UserAvatar
            user={friend}
            email={friend.email}
            src={friend.avatar}
            size={32}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200">
            <UserIdentity user={friend} showIdentifier={false} />
          </p>
          {secondaryIdentifier && (
            <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
              {secondaryIdentifier}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
        {onShareClick && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-${friend.email}`}
            title={
              friend.uid
                ? isSharing
                  ? "Manage availability sharing"
                  : "Share your availability"
                : "This friend hasn’t set up a profile yet"
            }
            disabled={!friend.uid}
            onClick={handleShare}
            className={
              isSharing
                ? "flex min-h-[36px] items-center gap-1 whitespace-nowrap rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                : "flex min-h-[36px] items-center gap-1 whitespace-nowrap rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-600"
            }
          >
            <Share2 className="h-3.5 w-3.5" />
            <span className="sm:hidden">Share</span>
            <span className="hidden sm:inline">{isSharing ? "Sharing" : "Share availability"}</span>
          </button>
        )}
        {onViewClick && (
          <button
            type="button"
            onClick={handleView}
            className="flex min-h-[36px] items-center whitespace-nowrap rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-600"
          >
            View
          </button>
        )}
        {onRemoveClick && (
          <button
            type="button"
            onClick={handleRemove}
            className="flex min-h-[36px] items-center whitespace-nowrap rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
